import { fetchUtils, HttpError } from 'react-admin';
import { stringify } from 'query-string';
import localStorageJWTManager from './localStorageJwtManager';
import { jwtDecode } from 'jwt-decode';
import { authProvider } from './authProvider';
import { fileTypeFromBlob } from 'file-type';
import { mappedFilters } from '@/components/global/filters/filters.jsx';
import { transformPeriodFilters } from '@/components/analytics/PeriodFilters.jsx';
import { i18nProvider } from '@/i18n';
import { normalizeDateFilterValue } from '@/helpers/date/dateValue';
import { isValueActive } from '@/helpers/filters/activeFilters';
import { normalizeDeviceConfigRecord } from '@/helpers/device/deviceConfig.js';

const api_url = import.meta.env.VITE_API_URL;
const api_client_url = import.meta.env.VITE_API_CLIENT_URL;
const api_service_url = import.meta.env.VITE_API_SERVICE_URL;

const getGlobalHttpErrorMessage = status => {
    if (status === 403) {
        return i18nProvider.translate('common.errors.forbidden');
    }
    if (status === 405) {
        return i18nProvider.translate('common.errors.methodNotAllowed');
    }
    return null;
};

const apiUrl = (relativeUrl, method) => {
    const userRoles = localStorageJWTManager.getUserRoles();
    if (!userRoles) return null;
    if (relativeUrl.startsWith('/notification') || relativeUrl.startsWith('/user-account')) return api_url;
    if (userRoles.includes('ROLE_ADMIN')) return api_url;
    if (userRoles.includes('ROLE_CLIENT_MASTER')) return api_client_url;
    if (userRoles.includes('ROLE_CLIENT')) return api_client_url;
    if (userRoles.includes('ROLE_POS_OWNER')) return api_client_url;
    if (userRoles.includes('ROLE_SERVICE_MASTER')) return api_service_url;
    if (userRoles.includes('ROLE_SERVICE')) return api_service_url;
    if (userRoles.includes('ROLE_BOK')) {
        return method === 'GET' ? api_url : api_client_url;
    }
    return null;
};

let refreshTokenPromise = null;

const refreshAuthToken = refreshToken => {
    if (!refreshTokenPromise) {
        refreshTokenPromise = authProvider
            .refreshToken(refreshToken)
            .then(response => {
                localStorageJWTManager.setToken(response.token);
                localStorageJWTManager.setRefreshToken(response.refresh_token);
                return response.token;
            })
            .finally(() => {
                refreshTokenPromise = null;
            });
    }

    return refreshTokenPromise;
};

const withAuthErrorHandling = promise =>
    promise.catch(error => {
        const globalErrorMessage = getGlobalHttpErrorMessage(error?.status);
        if (globalErrorMessage) {
            throw new HttpError(globalErrorMessage, error.status, error.body);
        }
        throw error;
    });

const httpClient = (relativeUrl, options = {}, blob = false, optionalBlob = false) => {
    const method = options.method || 'GET';
    const baseUrl = apiUrl(relativeUrl, method);
    const url = `${baseUrl}${relativeUrl}`;

    if (!options.headers) {
        options.headers = new Headers({ Accept: blob ? 'image/*,*/*' : 'application/json' });
    }

    const executeRequest = token => {
        options.headers.set('Authorization', `Bearer ${token}`);
        return withAuthErrorHandling(blob ? fetchBlob(url, options, optionalBlob) : fetchUtils.fetchJson(url, options));
    };

    const token = localStorageJWTManager.getToken();
    const refreshToken = localStorageJWTManager.getRefreshToken();
    try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.exp > Date.now() / 1000 + 30) {
            return executeRequest(token);
        }
    } catch (error) {
        if (!refreshToken) {
            return Promise.reject(new HttpError('Invalid token', 401, { errors: ['Invalid token'] }));
        }
    }

    if (!refreshToken) {
        return Promise.reject(new HttpError('Invalid token', 401, { errors: ['Invalid token'] }));
    }

    return refreshAuthToken(refreshToken).then(executeRequest);
};

const fetchBlob = (url, options = {}, optional = false) =>
    fetch(url, options)
        .then(response =>
            response.blob().then(blob => ({
                status: response.status,
                statusText: response.statusText,
                blob: blob,
            })),
        )
        .then(({ status, statusText, blob }) => {
            if (optional && status === 404) {
                return null;
            }
            if (status < 200 || status >= 300) {
                return Promise.reject(new Error(statusText, status));
            }
            return Promise.resolve(blob);
        });

const convertFileToBase64 = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const extractFiles = async data => {
    for (const field in data) {
        if (typeof data[field] !== 'object' || data[field] === null) {
            continue;
        }

        if (Object.hasOwn(data[field], 'rawFile')) {
            data[field] = {
                type: 'file',
                name: data[field].title || null,
                data: data[field].rawFile instanceof File ? await convertFileToBase64(data[field].rawFile) : null,
            };
            continue;
        }

        await extractFiles(data[field]);
    }
};

const prepareBody = async data => {
    const payload = structuredClone(data ?? {});
    await extractFiles(payload);
    return JSON.stringify(payload);
};

const resolveCreatedRecordId = (json, data) => {
    const candidates = [json?.id, data?.customId, data?.id];
    for (const candidate of candidates) {
        if (candidate !== undefined && candidate !== null && candidate !== '') {
            return candidate;
        }
    }
    return `created-${Date.now()}`;
};

const request = async (resource, params, method) => {
    const body = await prepareBody(params?.data ?? {});

    return httpClient(`/${resource}`, { method, body })
        .then(() => ({ data: {} }))
        .catch(error => {
            return ErrorTransformer.transform(error.body.errors, error.message, error.status);
        });
};

export const DataProvider = {
    getList: (resource, params) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const search = DataTransformer.transformSearch(params.filter);
        const withAggregates = DataTransformer.transformWithAggregates(params.filter);
        const filters = DataTransformer.transformFilters(params.filter, resource);

        const query = {
            sort: JSON.stringify({ field: field, order: order }),
            pagination: JSON.stringify({ page: page, perPage: perPage }),
            filters: JSON.stringify(filters),
            search: JSON.stringify(search),
            withAggregates: JSON.stringify(withAggregates),
        };

        const url = `/${resource}?${stringify(query)}`;

        return httpClient(url).then(({ json }) => ({
            data: json.data,
            total: json.total,
        }));
    },
    getBlob: async relativeUrl => httpClient(relativeUrl, {}, true),

    getOptionalBlob: async relativeUrl => httpClient(relativeUrl, {}, true, true),

    getOne: (resource, params) =>
        httpClient(`/${resource}/${params.id}`).then(({ json }) => ({
            data: resource === 'device-config' ? normalizeDeviceConfigRecord(json, params.id) : json,
        })),

    getMany: (resource, params) => {
        const query = {
            ids: JSON.stringify(DataTransformer.transformIds(params.ids)),
        };

        const url = `/${resource}?${stringify(query)}`;
        return httpClient(url).then(({ json }) => ({
            data: json.data,
        }));
    },

    update: async (resource, params) => {
        const body = await prepareBody(params.data);
        const method = body instanceof FormData ? 'POST' : 'PUT';
        return httpClient(`/${resource}/${params.id}`, {
            method,
            body,
        })
            .then(({ json }) => ({ data: json }))
            .catch(error => {
                return ErrorTransformer.transform(error.body.errors, error.message, error.status);
            });
    },

    import: (resource, params) =>
        httpClient(`/${resource}`, {
            method: 'POST',
            body: params.formData,
        }).then(({ status, json }) => ({ data: json, status: status })),
    create: async (resource, params) => {
        const body = await prepareBody(params.data);
        return httpClient(`/${resource}`, {
            method: 'POST',
            body,
        })
            .then(({ json }) => ({
                data: {
                    ...(json && typeof json === 'object' && !Array.isArray(json) ? json : {}),
                    id: resolveCreatedRecordId(json, params.data),
                },
            }))
            .catch(error => {
                return ErrorTransformer.transform(error.body.errors, error.message, error.status);
            });
    },

    updateMany: async (resource, params) => {
        const body = await prepareBody(params.data);

        return httpClient(`/${resource}`, {
            method: 'PUT',
            body,
        })
            .then(({ json }) => ({ data: json.data }))
            .catch(error => {
                return ErrorTransformer.transform(error.body.errors, error.message, error.status);
            });
    },

    deleteMany: (resource, params) =>
        httpClient(`/${resource}`, {
            method: 'DELETE',
            body: JSON.stringify({ ids: params.ids }),
        })
            .then(() => ({ data: [] }))
            .catch(error => {
                return ErrorTransformer.transform(error.body.errors, error.message, error.status);
            }),

    deleteOne: (resource, params) =>
        httpClient(`/${resource}/${params.id}/change/${params.changeId}`, {
            method: 'DELETE',
        })
            .then(() => ({ data: [] }))
            .catch(error => {
                return ErrorTransformer.transform(error.body.errors, error.message, error.status);
            }),

    delete: (resource, params) =>
        httpClient(`/${resource}/${params.id}`, {
            method: 'DELETE',
        })
            .then(() => ({ data: [] }))
            .catch(error => {
                return ErrorTransformer.transform(error.body.errors, error.message, error.status);
            }),

    export: (resource, params) => {
        const permanentFilter = structuredClone(params.filter || {});
        const filterValues = Object.fromEntries(Object.entries(params.filterValues || {}).filter(([, value]) => isValueActive(value)));
        const paramsFilter = { ...filterValues, ...permanentFilter };

        const { field, order } = params.sort;
        const search = DataTransformer.transformSearch(paramsFilter);
        const filters = DataTransformer.transformFilters(paramsFilter, resource);

        const query = {
            format: JSON.stringify({ value: params.format }),
            dataTransformer: JSON.stringify({ value: params.dataTransformer }),
            sort: JSON.stringify({ field: field, order: order }),
            filters: JSON.stringify(filters),
            search: JSON.stringify(search),
        };

        const url = `/${resource}/export?${stringify(query)}`;

        return httpClient(url).then(response => {
            const downloadLink = document.createElement('a');
            downloadLink.href = window.URL.createObjectURL(new Blob(['\ufeff' + response.body], { type: 'text/csv' }));
            downloadLink.setAttribute('download', `${resource}s.csv`);
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        });
    },
    summary: (resource, params) => {
        const paramfilters = JSON.parse(JSON.stringify(params.filter));
        const search = DataTransformer.transformSearch(paramfilters);
        const filters = DataTransformer.transformFilters(paramfilters, resource);

        const query = {
            filters: JSON.stringify(filters),
            search: JSON.stringify(search),
        };

        const url = `/${resource}?${stringify(query)}`;

        return httpClient(url).then(({ json }) => ({ data: json }));
    },
    globalSearch: ({ search, pagination, filters }) => {
        const transformedFilters = DataTransformer.transformFilters(filters);
        const query = {
            search: JSON.stringify(search),
            pagination: JSON.stringify(pagination),
            filters: JSON.stringify(transformedFilters),
        };
        const url = `/search?${stringify(query)}`;

        return httpClient(url).then(({ json }) => ({ data: json }));
    },
    downloadFile: async (relativeUrl, basename) => {
        const url = `${relativeUrl}`;
        const blob = await httpClient(url, {}, true);
        const fileType = await fileTypeFromBlob(blob);
        const name = fileType ? `${basename}.${fileType.ext}` : basename;

        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(blob);
        downloadLink.setAttribute('download', name);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    },
    post: async (resource, params) => request(resource, params, 'POST'),
    put: async (resource, params) => request(resource, params, 'PUT'),
    get: async (resource, params = {}) => {
        const url = `/${resource}?${stringify(params)}`;

        return httpClient(url).then(({ json }) => ({
            data: json.data,
        }));
    },

    getRaw: async relativeUrl =>
        httpClient(relativeUrl).then(({ json }) => ({
            data: json,
        })),
};

const SKIP_FILTER_KEYS = new Set(['search', 'q', 'withAggregates']);

const FILTER_OPERATORS = { _gte: '>=', _lte: '<=', _neq: '!=', _rel: '.', _in: '[]', _like: '%', _any: 'any', _nullish: '?=' };

const OPERATOR_SUFFIX_LENGTHS = [-4, -3, -5, -8];

const normalizeNullishFilterValue = (key, value) => {
    if ('materialId' === key && 'null' === value) return null;
    if (key === 'operator' && (value === null || value === 'null')) return null;
    if (key === 'couponSource' && (value === null || value === 'null')) return null;
    return value;
};

const getFilterFieldBaseName = key => {
    const withoutOperator = key.replace(/_(gte|lte|range)$/, '');
    const parts = withoutOperator.split('.');
    return parts[parts.length - 1];
};

const isDateFilterField = key => {
    const fieldName = getFilterFieldBaseName(key);
    return fieldName.endsWith('At') || fieldName.endsWith('Date') || fieldName.endsWith('Expires') || fieldName === 'periodStart' || fieldName === 'periodEnd';
};

const normalizeRangeFilterValue = (baseKey, value) => (isDateFilterField(baseKey) ? normalizeDateFilterValue(value) : value);

const hasRangeBoundValue = value => value !== '' && value != null;

const normalizeFilterKey = key => {
    let normalizedKey = key.includes('->') ? key.replaceAll('->', '.') : key;
    if (normalizedKey.slice(-6, -1) === 'quick') {
        normalizedKey = normalizedKey.slice(0, -7);
    }
    return normalizedKey;
};

const resolveMappedFilter = (key, value) => {
    if (key.slice(-4) !== '_map') {
        return { key, value };
    }

    const mappedFilter = mappedFilters[key.slice(0, -4)];
    if (!mappedFilter) {
        return null;
    }

    let resolvedKey = mappedFilter.key;
    let resolvedValue = mappedFilter.mapping[value] ?? null;
    if (resolvedValue === null) {
        return null;
    }

    if (typeof resolvedValue === 'object' && 'value' in resolvedValue && 'operator' in resolvedValue) {
        resolvedKey = `${resolvedKey}_${resolvedValue.operator}`;
        resolvedValue = resolvedValue.value;
    }

    return { key: resolvedKey, value: resolvedValue };
};

const applyRangeFilter = (key, value, transformedFilters) => {
    if (key.slice(-6) !== '_range') {
        return false;
    }

    const baseKey = key.slice(0, -6);
    if (Array.isArray(value) && value.length === 2) {
        const [fromValue, toValue] = value;
        if (hasRangeBoundValue(fromValue)) {
            transformedFilters[`${baseKey}_gte`] = normalizeRangeFilterValue(baseKey, fromValue);
        }
        if (hasRangeBoundValue(toValue)) {
            transformedFilters[`${baseKey}_lte`] = normalizeRangeFilterValue(baseKey, toValue);
        }
    }

    return true;
};

const applyDateBoundFilter = (key, value, transformedFilters) => {
    const isDateBound = (key.endsWith('_gte') || key.endsWith('_lte')) && typeof value === 'string' && isDateFilterField(key);
    if (!isDateBound) {
        return false;
    }

    transformedFilters[key] = normalizeDateFilterValue(value);
    return true;
};

const toFilterQueryItem = ([key, value]) => {
    for (const operatorSlice of OPERATOR_SUFFIX_LENGTHS) {
        const operator = FILTER_OPERATORS[key.slice(operatorSlice)];
        if (operator) {
            return { field: key.slice(0, operatorSlice), operator, value };
        }
    }

    return { field: key, operator: '=', value };
};

const DataTransformer = {
    transformFilters: (filters, resource) => {
        const transformedFilters = {};

        Object.entries(filters || {}).forEach(([rawKey, rawValue]) => {
            if (SKIP_FILTER_KEYS.has(rawKey)) {
                return;
            }

            const key = normalizeFilterKey(rawKey);
            const value = normalizeNullishFilterValue(key, rawValue);
            const mappedFilter = resolveMappedFilter(key, value);
            if (!mappedFilter) {
                return;
            }

            const { key: resolvedKey, value: resolvedValue } = mappedFilter;
            if (applyRangeFilter(resolvedKey, resolvedValue, transformedFilters)) {
                return;
            }
            if (applyDateBoundFilter(resolvedKey, resolvedValue, transformedFilters)) {
                return;
            }

            transformedFilters[resolvedKey] = resolvedValue;
        });

        const periodFilters = transformPeriodFilters(transformedFilters, resource);

        return Object.entries(periodFilters).map(toFilterQueryItem);
    },
    transformIds: ids => {
        for (let element in ids) {
            if ('null' === ids.at(element)) {
                ids[element] = null;
            }
        }

        return ids;
    },
    transformSearch: filters => {
        if (typeof filters.search === 'undefined') {
            return null;
        }

        const searchFields = [];
        Object.keys(filters.search.fields || {}).forEach(key => {
            if (true === filters.search.fields[key]) {
                const fieldName = key.includes('->') ? key.replace(/->/g, '.') : key;
                searchFields.push(fieldName);
            }
        });

        return {
            ...filters.search,
            fields: searchFields,
        };
    },
    transformWithAggregates: filters => {
        return filters.withAggregates === true;
    },
};

const ErrorTransformer = {
    transform: (errors, message, status) => {
        let error = null;
        if (errors && errors.length) {
            for (const errorKey in errors) {
                const errorObj = errors[errorKey];
                if ('field' in errorObj && 'message' in errorObj) {
                    const field = errorObj.field;

                    const contextParams = {};
                    if (Array.isArray(errorObj.context)) {
                        errorObj.context.forEach(item => {
                            if (item.key && item.value !== undefined) {
                                contextParams[item.key] = item.value;
                            }
                        });
                    }

                    const translatedMessage = i18nProvider.translate(errorObj.message, contextParams);

                    error = `[${field}] ${translatedMessage}`;
                    break;
                }
            }
        }
        const globalErrorMessage = getGlobalHttpErrorMessage(status);
        if (globalErrorMessage) {
            message = globalErrorMessage;
        }
        const fullMessage = error ? `${message} ${error}` : message;
        return Promise.reject(new HttpError(fullMessage, status));
    },
};
